import type { TableColumnType, TableRowType } from 'components/editor/grid/reduxStore/table.types';
import { CrmRowGroupRole } from 'components/editor/grid/reduxStore/table.types';
import { buildCrmChildRowId } from 'components/editor/grid/reduxStore/crmRowIds';
import type { VariableNode } from 'services/entities/VariablesEntity';
import type { ChipTemplateRef } from './deriveCrmRows';
import { buildCrmFieldInstances, collectRowChipTemplates } from './deriveCrmRows';
import type { CrmBindingFrame } from './crmChipBindings';
import { stampChipBindings } from './crmChipBindings';
import type { CrmInstance } from './crmInstanceIndex';
import { collectCrmInstances, isDescendantArrayTemplate, selectArrayTemplateForChips } from './crmInstanceIndex';
import type { AuthoredGroupPair } from './crmOrphanFamilies';
import { findAuthoredGroupPairs } from './crmOrphanFamilies';

export type GroupPlacement = { parent?: CrmInstance; children: CrmInstance[]; unrelatedFallback?: boolean };

export function planGroupRowPlacement(params: {
  parentChipTemplates: readonly string[];
  childChipTemplates: readonly string[];
  instances: readonly CrmInstance[];
}): GroupPlacement[] {
  const { parentChipTemplates, childChipTemplates, instances } = params;

  const parentArrayTemplate = selectArrayTemplateForChips(parentChipTemplates, instances);
  const childArrayTemplate = selectArrayTemplateForChips(childChipTemplates, instances);

  const parentSeries =
    parentArrayTemplate === undefined ? [] : instances.filter((instance) => instance.arrayTemplate === parentArrayTemplate);
  const childSeries = childArrayTemplate === undefined ? [] : instances.filter((instance) => instance.arrayTemplate === childArrayTemplate);

  if (parentArrayTemplate === undefined || parentSeries.length === 0) {
    return [{ parent: undefined, children: childSeries }];
  }

  const related = childArrayTemplate !== undefined && isDescendantArrayTemplate(childArrayTemplate, parentArrayTemplate);

  if (related) {
    return parentSeries.map((parent) => ({
      parent,
      children: childSeries.filter((child) => child.ancestorObjectIdByArrayTemplate[parentArrayTemplate] === parent.objectId),
    }));
  }

  const reversed = childArrayTemplate !== undefined && isDescendantArrayTemplate(parentArrayTemplate, childArrayTemplate);
  const unrelatedFallback = childArrayTemplate !== undefined && !reversed;

  return parentSeries.map((parent, index) => ({ parent, children: index === 0 ? childSeries : [], unrelatedFallback }));
}

export type DeriveGroupRowsParams = {
  rows: TableRowType[];
  columns: TableColumnType[];
  variablesRoot: VariableNode;
  enabled: boolean; // useCrmPricingGroupRowFlag() result, threaded in — never resolved here
  isLocked?: boolean;
};

function stampRowForFrame(
  row: TableRowType,
  columns: TableColumnType[],
  frame: CrmBindingFrame,
  ownChipTemplates: ReadonlySet<string>
): TableRowType {
  const stamped: TableRowType = { ...row };
  columns.forEach((column) => {
    const content = row[column.field];
    if (typeof content === 'string') {
      stamped[column.field] = stampChipBindings(content, frame, (chipName) => ownChipTemplates.has(chipName));
    }
  });
  return stamped;
}

function buildParentRows(
  parentRow: TableRowType,
  columns: TableColumnType[],
  parentChipTemplateSet: ReadonlySet<string>,
  parentChipRefs: ChipTemplateRef[],
  placements: readonly GroupPlacement[]
): TableRowType[] {
  const parentSeriesInstances = placements
    .map((placement) => placement.parent)
    .filter((instance): instance is CrmInstance => instance !== undefined);
  const parentFieldInstances = parentSeriesInstances.length > 0 ? buildCrmFieldInstances(parentSeriesInstances, parentChipRefs) : undefined;

  return placements.map((placement, index) => {
    const frame: CrmBindingFrame = placement.parent?.frame ?? {};
    const stamped = stampRowForFrame(parentRow, columns, frame, parentChipTemplateSet);

    if (index === 0) {
      if (placement.parent === undefined) {
        const { crmFieldInstances: _crmFieldInstances, crmInstanceObjectId: _crmInstanceObjectId, ...cleared } = stamped;
        return cleared;
      }
      return {
        ...stamped,
        crmInstanceObjectId: placement.parent.objectId,
        crmFieldInstances: parentFieldInstances,
        crmGroupUnrelatedFallback: placement.unrelatedFallback,
      };
    }

    const { crmFieldInstances: _crmFieldInstances, crmGroupPairedRowId: _crmGroupPairedRowId, ...stampedRest } = stamped;
    return {
      ...stampedRest,
      id: buildCrmChildRowId(parentRow.id, index),
      crmSourceRowId: parentRow.id,
      crmRowKind: 'auto-generated',
      crmRowGroupRole: CrmRowGroupRole.PARENT,
      crmInstanceObjectId: placement.parent?.objectId,
      crmGroupUnrelatedFallback: placement.unrelatedFallback,
    };
  });
}

function buildChildRowsByPlacement(
  childRow: TableRowType,
  columns: TableColumnType[],
  childChipTemplateSet: ReadonlySet<string>,
  placements: readonly GroupPlacement[],
  parentRows: readonly TableRowType[]
): TableRowType[][] {
  const childRowsByPlacementIndex: TableRowType[][] = placements.map(() => []);
  const totalChildren = placements.reduce((sum, placement) => sum + placement.children.length, 0);

  if (totalChildren === 0) {
    const stamped = stampRowForFrame(childRow, columns, {}, childChipTemplateSet);
    const { crmInstanceObjectId: _crmInstanceObjectId, crmGroupInstanceParentRowId: _crmGroupInstanceParentRowId, ...cleared } = stamped;
    childRowsByPlacementIndex[0] = [cleared];
    return childRowsByPlacementIndex;
  }

  let n = 0;
  placements.forEach((placement, index) => {
    const parentRowId = parentRows[index].id;
    placement.children.forEach((instance) => {
      const stamped = stampRowForFrame(childRow, columns, instance.frame, childChipTemplateSet);
      const { crmFieldInstances: _crmFieldInstances, crmGroupPairedRowId: _crmGroupPairedRowId, ...stampedRest } = stamped;

      const materialized: TableRowType =
        n === 0
          ? {
              ...stamped,
              crmInstanceObjectId: instance.objectId,
              crmGroupInstanceParentRowId: parentRowId,
              crmGroupUnrelatedFallback: placement.unrelatedFallback,
            }
          : {
              ...stampedRest,
              id: buildCrmChildRowId(childRow.id, n),
              crmSourceRowId: childRow.id,
              crmRowKind: 'auto-generated',
              crmRowGroupRole: CrmRowGroupRole.CHILD,
              crmInstanceObjectId: instance.objectId,
              crmGroupInstanceParentRowId: parentRowId,
              crmGroupUnrelatedFallback: placement.unrelatedFallback,
            };

      childRowsByPlacementIndex[index].push(materialized);
      n += 1;
    });
  });

  return childRowsByPlacementIndex;
}

function applyGroupPair(
  rows: TableRowType[],
  columns: TableColumnType[],
  variablesRoot: VariableNode,
  pair: AuthoredGroupPair
): TableRowType[] {
  const { parentRow, childRow } = pair;

  const parentChipRefs = collectRowChipTemplates(parentRow, columns);
  const childChipRefs = collectRowChipTemplates(childRow, columns);
  const parentChipTemplateSet = new Set(parentChipRefs.map((ref) => ref.template));
  const childChipTemplateSet = new Set(childChipRefs.map((ref) => ref.template));

  const allChipTemplates = new Set<string>([...parentChipTemplateSet, ...childChipTemplateSet]);
  const instances = collectCrmInstances(variablesRoot, allChipTemplates);

  const placements = planGroupRowPlacement({
    parentChipTemplates: Array.from(parentChipTemplateSet),
    childChipTemplates: Array.from(childChipTemplateSet),
    instances,
  });

  const parentRows = buildParentRows(parentRow, columns, parentChipTemplateSet, parentChipRefs, placements);
  const childRowsByPlacementIndex = buildChildRowsByPlacement(childRow, columns, childChipTemplateSet, placements, parentRows);

  const block: TableRowType[] = [];
  placements.forEach((_, index) => {
    block.push(parentRows[index]);
    block.push(...childRowsByPlacementIndex[index]);
  });

  const staleGeneratedIds = new Set(
    rows
      .filter((row) => row.crmRowKind === 'auto-generated' && (row.crmSourceRowId === parentRow.id || row.crmSourceRowId === childRow.id))
      .map((row) => row.id)
  );

  const result: TableRowType[] = [];
  rows.forEach((row) => {
    if (staleGeneratedIds.has(row.id)) return;
    if (row.id === childRow.id) return; // emitted inside the block, at the parent row's position
    if (row.id === parentRow.id) {
      result.push(...block);
      return;
    }
    result.push(row);
  });

  return result;
}

export function deriveGroupRows(params: DeriveGroupRowsParams): TableRowType[] {
  const { rows, columns, variablesRoot, enabled, isLocked = false } = params;

  if (!enabled) return rows;
  if (isLocked) return rows;

  const pairs = findAuthoredGroupPairs(rows);
  if (pairs.length === 0) return rows;

  return pairs.reduce((currentRows, pair) => applyGroupPair(currentRows, columns, variablesRoot, pair), rows);
}
